// ERP Connections Page
// Manage ERP system connections with support for Odoo and Acumatica

import { useState, useEffect, useRef } from 'react';
import { trpc } from '../../lib/trpc';
import { format } from 'date-fns';

// ============================================================
// TYPES
// ============================================================

type ERPType = 'odoo' | 'acumatica' | 'quickbooks' | 'sage' | 'netsuite' | 'xero';
type SupportedERPType = 'odoo' | 'acumatica' | 'quickbooks';

// ============================================================
// ERP TYPE CONFIGURATION
// ============================================================

interface ERPConfig {
  name: string;
  logo: string;
  urlLabel: string;
  urlPlaceholder: string;
  urlHint: string;
  databaseLabel: string;
  databasePlaceholder: string;
  supportsApiKey: boolean;
  apiKeyHint: string;
  requiresOAuth: boolean;
  oauthButtonText?: string;
}

const ERP_CONFIG: Record<SupportedERPType, ERPConfig> = {
  odoo: {
    name: 'Odoo',
    logo: '/assets/erp/odoo.png',
    urlLabel: 'Odoo URL',
    urlPlaceholder: 'https://your-company.odoo.com',
    urlHint: 'The URL of your Odoo instance (Odoo 14+ required)',
    databaseLabel: 'Database Name',
    databasePlaceholder: 'your-database',
    supportsApiKey: true,
    apiKeyHint: 'Generate an API key in Odoo under Settings → Users → API Keys',
    requiresOAuth: false,
  },
  acumatica: {
    name: 'Acumatica',
    logo: '/assets/erp/acumatica.png',
    urlLabel: 'Acumatica URL',
    urlPlaceholder: 'https://erp.yourcompany.com/AcumaticaERP/',
    urlHint: 'The URL of your Acumatica instance (include /AcumaticaERP/ path)',
    databaseLabel: 'Tenant / Company',
    databasePlaceholder: 'Your Company',
    supportsApiKey: false,
    apiKeyHint: '',
    requiresOAuth: false,
  },
  quickbooks: {
    name: 'QuickBooks',
    logo: '/assets/erp/quickbooks.png',
    urlLabel: '',
    urlPlaceholder: '',
    urlHint: '',
    databaseLabel: '',
    databasePlaceholder: '',
    supportsApiKey: false,
    apiKeyHint: '',
    requiresOAuth: true,
    oauthButtonText: 'Connect with QuickBooks',
  },
};

// Logo paths for all ERP types (including unsupported/coming soon)
const ERP_LOGOS: Record<ERPType, string> = {
  odoo: '/assets/erp/odoo.png',
  acumatica: '/assets/erp/acumatica.png',
  quickbooks: '/assets/erp/quickbooks.png',
  sage: '/assets/erp/sage.svg',
  netsuite: '/assets/erp/netsuite.svg',
  xero: '/assets/erp/xero.svg',
};

// Helper to safely get ERP config (returns undefined for unsupported types)
function getErpConfig(erpType: ERPType): ERPConfig | undefined {
  if (erpType === 'odoo' || erpType === 'acumatica' || erpType === 'quickbooks') {
    return ERP_CONFIG[erpType];
  }
  return undefined;
}

// Get logo for any ERP type (including unsupported)
function getErpLogo(erpType: ERPType): string {
  return ERP_LOGOS[erpType] ?? '/assets/erp/odoo.svg';
}

interface ERPConnection {
  id: string;
  name: string;
  erpType: ERPType;
  url: string;
  database: string;
  username: string;
  status: 'active' | 'error' | 'disconnected';
  lastValidatedAt: string | null;
  lastSyncAt: string | null;
  serverVersion: string | null;
  settings?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  name: string;
  erpType: SupportedERPType; // Only supported ERP types can be selected
  url: string;
  database: string;
  username: string;
  password: string;
  apiKey: string;
  acumaticaEndpointVersion: string;
  useApiKey: boolean;
  // QuickBooks OAuth fields
  qbClientId: string;
  qbClientSecret: string;
  qbEnvironment: 'sandbox' | 'production';
}

const initialFormData: FormData = {
  name: '',
  erpType: 'odoo',
  url: '',
  database: '',
  username: '',
  password: '',
  apiKey: '',
  acumaticaEndpointVersion: '23.200.001',
  useApiKey: false,
  qbClientId: '',
  qbClientSecret: '',
  qbEnvironment: 'sandbox',
};

// ============================================================
// STATUS BADGE COMPONENT
// ============================================================

function StatusBadge({ status }: { status: 'active' | 'error' | 'disconnected' }) {
  const styles = {
    active: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
    disconnected: 'bg-gray-100 text-gray-600',
  };

  const labels = {
    active: 'Connected',
    error: 'Error',
    disconnected: 'Disconnected',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${status === 'active' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-gray-400'}`} />
      {labels[status]}
    </span>
  );
}

// ============================================================
// CONNECTION FORM MODAL
// ============================================================

interface TestResult {
  success: boolean;
  message: string;
  rawError?: string; // Full error details for debugging
}

interface ConnectionFormModalProps {
  isOpen: boolean;
  editConnection: ERPConnection | null;
  onClose: () => void;
  onSave: (data: FormData, skipNameWarning?: boolean) => void;
  onTest: (data: FormData) => void;
  isSaving: boolean;
  isTesting: boolean;
  testResult: TestResult | null;
}

function ConnectionFormModal({
  isOpen,
  editConnection,
  onClose,
  onSave,
  onTest,
  isSaving,
  isTesting,
  testResult,
}: ConnectionFormModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [showNameWarning, setShowNameWarning] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // Reset form when modal opens or edit connection changes
  useEffect(() => {
    if (isOpen) {
      if (editConnection) {
        // Validate that the ERP type is supported (it should be, since we only allow adding supported types)
        const erpType: SupportedERPType =
          editConnection.erpType === 'odoo' || editConnection.erpType === 'acumatica' || editConnection.erpType === 'quickbooks'
            ? editConnection.erpType
            : 'odoo'; // Fallback to odoo if somehow an unsupported type exists

        setFormData({
          name: editConnection.name,
          erpType,
          url: editConnection.url,
          database: editConnection.database,
          username: editConnection.username,
          password: '',
          apiKey: '',
          acumaticaEndpointVersion:
            typeof editConnection.settings?.['acumaticaEndpointVersion'] === 'string'
              ? editConnection.settings['acumaticaEndpointVersion'] as string
              : '23.200.001',
          useApiKey: false,
          qbClientId: '',
          qbClientSecret: '',
          qbEnvironment: 'sandbox',
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
      setShowNameWarning(false);
      setShowErrorDetails(false);
    }
  }, [isOpen, editConnection]);

  const validate = (requireCredentials = true): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    const erpConfig = ERP_CONFIG[formData.erpType];

    if (!formData.name.trim()) {
      newErrors.name = 'Connection name is required';
    }

    // QuickBooks uses OAuth - different validation
    if (erpConfig?.requiresOAuth) {
      if (!formData.qbClientId.trim()) {
        newErrors.qbClientId = 'Client ID is required';
      }
      if (!formData.qbClientSecret.trim()) {
        newErrors.qbClientSecret = 'Client Secret is required';
      }
    } else {
      // Standard ERP validation (Odoo, Acumatica)
      if (!formData.url.trim()) {
        newErrors.url = `${erpConfig?.urlLabel || 'URL'} is required`;
      } else if (!formData.url.startsWith('http://') && !formData.url.startsWith('https://')) {
        newErrors.url = 'URL must start with http:// or https://';
      }

      if (!formData.database.trim()) {
        newErrors.database = `${erpConfig?.databaseLabel || 'Database'} is required`;
      }

      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      }

      if (formData.erpType === 'acumatica' && !formData.acumaticaEndpointVersion.trim()) {
        newErrors.acumaticaEndpointVersion = 'Default endpoint version is required';
      }

      // Always require credentials for testing and saving (even when editing)
      if (requireCredentials) {
        // Only check API key if the ERP type supports it and user selected API key auth
        if (formData.useApiKey && erpConfig?.supportsApiKey) {
          if (!formData.apiKey.trim()) {
            newErrors.apiKey = 'API key is required';
          }
        } else {
          if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Check if name changed during edit - show warning
      if (editConnection && formData.name !== editConnection.name) {
        setShowNameWarning(true);
      } else {
        onSave(formData);
      }
    }
  };

  const handleConfirmNameChange = () => {
    setShowNameWarning(false);
    onSave(formData, true);
  };

  const handleTest = () => {
    if (validate()) {
      onTest(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {editConnection ? 'Edit Connection' : `Add ${ERP_CONFIG[formData.erpType]?.name || 'ERP'} Connection`}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Edit Mode Notice */}
            {editConnection && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <strong>Security Notice:</strong> The password {ERP_CONFIG[formData.erpType]?.supportsApiKey ? 'or API key ' : ''}must be entered again to save changes. If you don't have the credentials, click Cancel to exit without changes.
                  </div>
                </div>
              </div>
            )}

            {/* ERP Type Selector - Only for new connections */}
            {!editConnection && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ERP System
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(['odoo', 'acumatica', 'quickbooks'] as const).map((type) => {
                    const config = ERP_CONFIG[type];
                    const isSelected = formData.erpType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData({
                          ...initialFormData,
                          name: formData.name, // Preserve name
                          erpType: type,
                        })}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center p-1.5 ${
                          isSelected ? 'bg-primary-50' : 'bg-gray-50'
                        }`}>
                          <img src={config.logo} alt={config.name} className="w-full h-full object-contain" />
                        </div>
                        <div className="text-left">
                          <div className={`font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                            {config.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {type === 'odoo' ? 'XML-RPC' : type === 'quickbooks' ? 'OAuth 2.0' : 'REST API'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Connection Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Connection Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Production, Sandbox"
                className={`input ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
            </div>

            {/* QuickBooks OAuth Fields */}
            {ERP_CONFIG[formData.erpType]?.requiresOAuth ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      QuickBooks uses OAuth 2.0 for secure authentication. Enter your Intuit Developer app credentials, then click <strong>Connect with QuickBooks</strong> to authorize access.
                    </div>
                  </div>
                </div>

                {/* Client ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={formData.qbClientId}
                    onChange={(e) => setFormData({ ...formData, qbClientId: e.target.value })}
                    placeholder="Enter your Intuit Developer app Client ID"
                    className={`input ${errors.qbClientId ? 'border-red-500' : ''}`}
                  />
                  {errors.qbClientId && <p className="text-sm text-red-600 mt-1">{errors.qbClientId}</p>}
                </div>

                {/* Client Secret */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Secret
                  </label>
                  <input
                    type="password"
                    value={formData.qbClientSecret}
                    onChange={(e) => setFormData({ ...formData, qbClientSecret: e.target.value })}
                    placeholder="Enter your Intuit Developer app Client Secret"
                    className={`input ${errors.qbClientSecret ? 'border-red-500' : ''}`}
                  />
                  {errors.qbClientSecret && <p className="text-sm text-red-600 mt-1">{errors.qbClientSecret}</p>}
                </div>

                {/* Environment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Environment
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['sandbox', 'production'] as const).map((env) => (
                      <button
                        key={env}
                        type="button"
                        onClick={() => setFormData({ ...formData, qbEnvironment: env })}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                          formData.qbEnvironment === env
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {env === 'sandbox' ? 'Sandbox (Testing)' : 'Production (Live)'}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Use Sandbox for testing. Switch to Production when ready to go live.
                  </p>
                </div>

                <div className="text-xs text-gray-500 border-t border-gray-200 pt-3">
                  <strong>How to get credentials:</strong> Sign in to the{' '}
                  <a
                    href="https://developer.intuit.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 underline hover:text-primary-700"
                  >
                    Intuit Developer Portal
                  </a>
                  , create an app, and copy the Client ID and Client Secret from your app's Keys & credentials page.
                </div>
              </>
            ) : (
              <>
                {/* Standard ERP Fields (Odoo, Acumatica) */}

                {/* Server URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {ERP_CONFIG[formData.erpType]?.urlLabel || 'Server URL'}
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder={ERP_CONFIG[formData.erpType]?.urlPlaceholder || 'https://...'}
                    className={`input ${errors.url ? 'border-red-500' : ''}`}
                  />
                  {errors.url && <p className="text-sm text-red-600 mt-1">{errors.url}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    {ERP_CONFIG[formData.erpType]?.urlHint || 'The URL of your ERP instance'}
                  </p>
                </div>

                {/* Database / Tenant */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {ERP_CONFIG[formData.erpType]?.databaseLabel || 'Database'}
                  </label>
                  <input
                    type="text"
                    value={formData.database}
                    onChange={(e) => setFormData({ ...formData, database: e.target.value })}
                    placeholder={ERP_CONFIG[formData.erpType]?.databasePlaceholder || 'database'}
                    className={`input ${errors.database ? 'border-red-500' : ''}`}
                  />
                  {errors.database && <p className="text-sm text-red-600 mt-1">{errors.database}</p>}
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username / Email
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="user@example.com"
                    className={`input ${errors.username ? 'border-red-500' : ''}`}
                  />
                  {errors.username && <p className="text-sm text-red-600 mt-1">{errors.username}</p>}
                </div>

                {formData.erpType === 'acumatica' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Endpoint Version
                    </label>
                    <input
                      type="text"
                      value={formData.acumaticaEndpointVersion}
                      onChange={(e) => setFormData({ ...formData, acumaticaEndpointVersion: e.target.value })}
                      placeholder="23.200.001"
                      className={`input ${errors.acumaticaEndpointVersion ? 'border-red-500' : ''}`}
                    />
                    {errors.acumaticaEndpointVersion && <p className="text-sm text-red-600 mt-1">{errors.acumaticaEndpointVersion}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      The Acumatica Default endpoint version, for example <code>23.200.001</code>.
                    </p>
                  </div>
                )}

                {/* Authentication Method Toggle - Only for ERPs that support API keys */}
                {ERP_CONFIG[formData.erpType]?.supportsApiKey && (
                  <div className="pt-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.useApiKey}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            useApiKey: e.target.checked,
                            password: '',
                            apiKey: '',
                          })
                        }
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Use API Key instead of password</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-7">
                      API keys are recommended for production environments
                    </p>
                  </div>
                )}

                {/* Password or API Key */}
                {formData.useApiKey && ERP_CONFIG[formData.erpType]?.supportsApiKey ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key
                    </label>
                    <input
                      type="password"
                      value={formData.apiKey}
                      onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                      placeholder={editConnection ? '••••••••••••' : `Enter your ${ERP_CONFIG[formData.erpType]?.name || 'ERP'} API key`}
                      className={`input ${errors.apiKey ? 'border-red-500' : ''}`}
                    />
                    {errors.apiKey && <p className="text-sm text-red-600 mt-1">{errors.apiKey}</p>}
                    {ERP_CONFIG[formData.erpType]?.apiKeyHint && (
                      <p className="text-xs text-gray-500 mt-1">
                        {ERP_CONFIG[formData.erpType].apiKeyHint}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={editConnection ? '••••••••••••' : `Enter your ${ERP_CONFIG[formData.erpType]?.name || 'ERP'} password`}
                      className={`input ${errors.password ? 'border-red-500' : ''}`}
                    />
                    {errors.password && <p className="text-sm text-red-600 mt-1">{errors.password}</p>}
                  </div>
                )}
              </>
            )}

            {/* Test Result */}
            {testResult && (
              <div
                className={`p-3 rounded-lg ${
                  testResult.success
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className="text-sm font-medium">{testResult.message}</span>
                </div>

                {/* Expandable Error Details */}
                {!testResult.success && testResult.rawError && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                      className="text-xs text-red-600 hover:text-red-700 underline flex items-center gap-1"
                    >
                      <svg
                        className={`w-3 h-3 transition-transform ${showErrorDetails ? 'rotate-90' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {showErrorDetails ? 'Hide Details' : 'Show Details'}
                    </button>
                    {showErrorDetails && (
                      <div className="mt-2 p-2 bg-red-100 rounded text-xs font-mono whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                        {testResult.rawError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              {ERP_CONFIG[formData.erpType]?.requiresOAuth ? (
                /* OAuth flow: single "Connect with QuickBooks" button */
                <button
                  type="submit"
                  disabled={isSaving || isTesting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Connecting...
                    </span>
                  ) : (
                    ERP_CONFIG[formData.erpType]?.oauthButtonText ?? 'Connect'
                  )}
                </button>
              ) : (
                /* Standard flow: Test + Save buttons */
                <>
                  <button
                    type="button"
                    onClick={handleTest}
                    disabled={isTesting || isSaving}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isTesting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Testing...
                      </span>
                    ) : (
                      'Test Connection'
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving || isTesting}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : editConnection ? 'Update Connection' : 'Save Connection'}
                  </button>
                </>
              )}
            </div>
          </form>

          {/* Name Change Warning Modal */}
          {showNameWarning && (
            <div className="absolute inset-0 bg-white rounded-xl p-6 flex flex-col">
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Change Connection Name?
                </h3>
                <p className="text-gray-600 mb-2">
                  You are changing the connection name from:
                </p>
                <p className="text-sm mb-4">
                  <span className="font-medium text-gray-900">"{editConnection?.name}"</span>
                  <span className="text-gray-500 mx-2">→</span>
                  <span className="font-medium text-primary-600">"{formData.name}"</span>
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <strong>Warning:</strong> Updating the Connection Name could affect functions in your spreadsheets that reference this Connection Name. Are you sure you want to update this value?
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowNameWarning(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmNameChange}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700"
                >
                  Yes, Update Name
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DELETE CONFIRMATION MODAL
// ============================================================

interface DeleteConfirmModalProps {
  isOpen: boolean;
  connectionName: string;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

function DeleteConfirmModal({
  isOpen,
  connectionName,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteConfirmModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Connection?</h3>
          <p className="text-gray-600 mb-4">
            Are you sure you want to delete <strong>{connectionName}</strong>? This will also remove all synced companies, accounts, and GL data associated with this connection. This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {isDeleting ? 'Deleting...' : 'Delete Connection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CONNECTION CARD COMPONENT
// ============================================================

interface ConnectionCardProps {
  connection: ERPConnection;
  onEdit: (connection: ERPConnection) => void;
  onDelete: (connection: ERPConnection) => void;
  onSync: (connectionId: string) => void;
  onTest: (connectionId: string) => void;
  isSyncing: boolean;
  isTesting: boolean;
  testResult: { connectionId: string; success: boolean; message: string } | null;
}

function ConnectionCard({ connection, onEdit, onDelete, onSync, onTest, isSyncing, isTesting, testResult }: ConnectionCardProps) {
  const erpConfig = getErpConfig(connection.erpType) ?? { name: connection.erpType, logo: getErpLogo(connection.erpType) };
  const cardTestResult = testResult?.connectionId === connection.id ? testResult : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
      <div className="flex flex-col items-center mb-4">
        <div className="h-16 flex items-center justify-center mb-3">
          <img src={erpConfig.logo} alt={erpConfig.name} className="max-h-full object-contain" style={{ width: '140px' }} />
        </div>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-gray-900">{connection.name}</h3>
          <StatusBadge status={connection.status} />
        </div>
        <p className="text-sm text-gray-500">{erpConfig.name} {connection.serverVersion || ''}</p>
      </div>

      <div className="space-y-2 text-sm mb-4">
        {connection.erpType === 'quickbooks' ? (
          /* QuickBooks OAuth connection details */
          <div className="flex items-center gap-2 text-gray-600">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Connected via OAuth 2.0</span>
          </div>
        ) : (
          /* Standard ERP connection details */
          <>
            <div className="flex items-center gap-2 text-gray-600">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <span className="truncate">{connection.url}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              <span>{connection.database}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{connection.username}</span>
            </div>
          </>
        )}
      </div>

      {/* Last sync info */}
      <div className="text-xs text-gray-500 mb-4 space-y-1">
        {connection.lastSyncAt && (
          <p>Last synced: {format(new Date(connection.lastSyncAt), 'MMM d, yyyy h:mm a')}</p>
        )}
        {connection.lastValidatedAt && (
          <p>Last validated: {format(new Date(connection.lastValidatedAt), 'MMM d, yyyy h:mm a')}</p>
        )}
      </div>

      {/* Inline test result */}
      {cardTestResult && (
        <div className={`mb-4 p-2.5 rounded-lg text-sm ${
          cardTestResult.success
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {cardTestResult.success ? (
              <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span>{cardTestResult.message}</span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t border-gray-100">
        <button
          onClick={() => onSync(connection.id)}
          disabled={isSyncing || connection.status !== 'active'}
          className="flex-1 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSyncing ? 'Syncing...' : 'Sync Data'}
        </button>
        <button
          onClick={() => onTest(connection.id)}
          disabled={isTesting}
          className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isTesting ? (
            <span className="flex items-center gap-1.5">
              <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Testing...
            </span>
          ) : 'Test'}
        </button>
        <button
          onClick={() => onEdit(connection)}
          className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(connection)}
          className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

export function ErpConnectionsPage() {
  const [showFormModal, setShowFormModal] = useState(false);
  const [editConnection, setEditConnection] = useState<ERPConnection | null>(null);
  const [deleteConnection, setDeleteConnection] = useState<ERPConnection | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [syncingConnectionId, setSyncingConnectionId] = useState<string | null>(null);

  // State for configuration change confirmation
  const [pendingConfigChange, setPendingConfigChange] = useState<{
    formData: FormData;
    changes: string[];
    dataToDelete: { companies: number; accounts: number; glEntries: number };
  } | null>(null);

  // Ref to store form data between mutation call and confirmation
  const pendingFormDataRef = useRef<FormData | null>(null);

  // Fetch tenant data for limits
  const { data: tenant } = trpc.tenant.getCurrent.useQuery();
  const { data: usage, refetch: refetchUsage } = trpc.tenant.getUsage.useQuery();

  // Fetch connections
  const { data: connections, isLoading: connectionsLoading, refetch: refetchConnections } = trpc.erp.listConnections.useQuery();

  // Connection limit check
  const connectionLimit = tenant?.limits?.connections ?? 1;
  const currentConnections = usage?.connections?.current ?? 0;
  const canAddConnection = connectionLimit === -1 || currentConnections < connectionLimit;

  // Mutations
  const createMutation = trpc.erp.createConnection.useMutation({
    onSuccess: () => {
      refetchConnections();
      refetchUsage(); // Refresh connection count for limit check
      setShowFormModal(false);
      setEditConnection(null);
      setTestResult(null);
    },
    onError: (error) => {
      const rawError = error.message || 'Unknown error';
      const isConnectionError = rawError.toLowerCase().includes('connect') ||
                                rawError.toLowerCase().includes('authentication');
      setTestResult({
        success: false,
        message: isConnectionError
          ? 'Failed to connect. One or more connection parameters may be incorrect.'
          : 'Failed to save connection. Please try again.',
        rawError: rawError,
      });
    },
  });

  const updateMutation = trpc.erp.updateConnection.useMutation({
    onSuccess: (result) => {
      // Check if this is a confirmation request (URL or database changed)
      // Use !result.success for proper type narrowing on the discriminated union
      if (!result.success) {
        // Store the pending change data - we need to show confirmation dialog
        // The formData will be set by handleSave before calling mutate
        setPendingConfigChange({
          formData: pendingFormDataRef.current!,
          changes: result.changes,
          dataToDelete: result.dataToDelete,
        });
        return;
      }

      const updatedConnectionId = editConnection?.id ?? null;

      // Actual success - close modal and refresh
      refetchConnections();
      setShowFormModal(false);
      setEditConnection(null);
      setTestResult(null);
      setPendingConfigChange(null);

      if (result.configurationChanged && updatedConnectionId) {
        setSyncingConnectionId(updatedConnectionId);
        syncMutation.mutate({ connectionId: updatedConnectionId });
      }
    },
    onError: (error) => {
      // Close confirmation dialog if open - error will show in main form
      setPendingConfigChange(null);
      pendingFormDataRef.current = null;

      const rawError = error.message || 'Unknown error';
      const isConnectionError = rawError.toLowerCase().includes('connect') ||
                                rawError.toLowerCase().includes('authentication') ||
                                rawError.toLowerCase().includes('database');
      setTestResult({
        success: false,
        message: isConnectionError
          ? 'Connection test failed. One or more connection parameters may be incorrect.'
          : 'Failed to update connection. Please try again.',
        rawError: rawError,
      });
    },
  });

  const deleteMutation = trpc.erp.deleteConnection.useMutation({
    onSuccess: () => {
      refetchConnections();
      refetchUsage(); // Refresh connection count for limit check
      setDeleteConnection(null);
    },
  });

  // Track the ERP type being tested for proper success message
  const [testingErpType, setTestingErpType] = useState<SupportedERPType>('odoo');

  const testMutation = trpc.erp.testConnection.useMutation({
    onSuccess: (data) => {
      const erpName = ERP_CONFIG[testingErpType].name;
      // Backend returns { success: boolean, error?: string, serverInfo?: object } - check the actual result
      if (data.success) {
        const versionInfo = data.serverInfo?.version ? ` version: ${data.serverInfo.version}` : '';
        setTestResult({
          success: true,
          message: `Connection successful! ${erpName}${versionInfo}`,
        });
      } else {
        // Provide a user-friendly message, but keep raw error for debugging
        const rawError = data.error || 'Unknown error';
        const isAuthError = rawError.toLowerCase().includes('authentication') ||
                           rawError.toLowerCase().includes('password') ||
                           rawError.toLowerCase().includes('access denied') ||
                           rawError.toLowerCase().includes('401');

        setTestResult({
          success: false,
          message: isAuthError
            ? 'Authentication failed. Please check your username and password.'
            : 'Connection failed. One or more connection parameters may be incorrect.',
          rawError: rawError,
        });
      }
    },
    onError: (error) => {
      const rawError = error.message || 'Unknown error';
      setTestResult({
        success: false,
        message: 'Connection failed. One or more connection parameters may be incorrect.',
        rawError: rawError,
      });
    },
  });

  // Validate existing connection by ID (uses stored credentials)
  const [validatingConnectionId, setValidatingConnectionId] = useState<string | null>(null);
  const [cardTestResult, setCardTestResult] = useState<{ connectionId: string; success: boolean; message: string } | null>(null);

  const validateMutation = trpc.erp.validateConnection.useMutation({
    onSuccess: (data) => {
      const connectionId = validatingConnectionId;
      if (connectionId) {
        const versionInfo = data.serverInfo?.version ? ` (v${data.serverInfo.version})` : '';
        setCardTestResult({
          connectionId,
          success: data.success ?? false,
          message: data.success
            ? `Connection successful!${versionInfo}`
            : data.error ?? 'Connection failed.',
        });
      }
      setValidatingConnectionId(null);
      refetchConnections(); // Refresh to show updated status/last_validated_at
    },
    onError: (error) => {
      const connectionId = validatingConnectionId;
      if (connectionId) {
        setCardTestResult({
          connectionId,
          success: false,
          message: error.message || 'Connection test failed.',
        });
      }
      setValidatingConnectionId(null);
    },
  });

  // State for sync error messages
  const [syncError, setSyncError] = useState<{ connectionId: string; message: string } | null>(null);

  const syncMutation = trpc.erp.triggerFullSync.useMutation({
    onSuccess: (data) => {
      const connectionId = syncingConnectionId;
      setSyncingConnectionId(null);
      setSyncError(null);
      refetchConnections();
      // Show success message briefly via cardTestResult
      if (connectionId) {
        setCardTestResult({
          connectionId,
          success: true,
          message: `Sync started! Job ID: ${data.jobId}`,
        });
      }
    },
    onError: (error) => {
      const connectionId = syncingConnectionId;
      setSyncingConnectionId(null);
      // Show error to user
      if (connectionId) {
        const errorMsg = error.message || 'Sync failed. Please try again.';
        // Check for common issues
        const isRedisError = errorMsg.toLowerCase().includes('redis') ||
                            errorMsg.toLowerCase().includes('econnrefused') ||
                            errorMsg.toLowerCase().includes('connection');
        setSyncError({
          connectionId,
          message: isRedisError
            ? 'Sync service unavailable. Please ensure Redis is running.'
            : errorMsg,
        });
        setCardTestResult({
          connectionId,
          success: false,
          message: isRedisError
            ? 'Sync service unavailable. Please ensure Redis is running.'
            : `Sync failed: ${errorMsg}`,
        });
      }
    },
  });

  // Handlers
  const handleOpenForm = (connection?: ERPConnection) => {
    setEditConnection(connection || null);
    setTestResult(null);
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setShowFormModal(false);
    setEditConnection(null);
    setTestResult(null);
  };

  // QuickBooks OAuth initiation mutation
  const qbOAuthMutation = trpc.erp.initiateQuickBooksOAuth.useMutation({
    onSuccess: (data) => {
      // Redirect user to QuickBooks authorization page
      window.location.href = data.authorizationUrl;
    },
    onError: (error) => {
      setTestResult({
        success: false,
        message: 'Failed to initiate QuickBooks connection.',
        rawError: error.message,
      });
    },
  });

  const handleSave = (data: FormData, _skipNameWarning?: boolean) => {
    // skipNameWarning is handled by the modal - if we get here, user confirmed

    // QuickBooks uses OAuth - initiate the flow instead of direct save
    if (ERP_CONFIG[data.erpType]?.requiresOAuth) {
      qbOAuthMutation.mutate({
        name: data.name,
        clientId: data.qbClientId,
        clientSecret: data.qbClientSecret,
        environment: data.qbEnvironment,
        ...(editConnection ? { connectionId: editConnection.id } : {}),
      });
      return;
    }

    if (editConnection) {
      // Store form data in ref for potential confirmation callback
      pendingFormDataRef.current = data;
      updateMutation.mutate({
        connectionId: editConnection.id,
        name: data.name,
        url: data.url,
        database: data.database,
        username: data.username,
        ...(data.erpType === 'acumatica' ? { acumaticaEndpointVersion: data.acumaticaEndpointVersion } : {}),
        ...(data.useApiKey && data.apiKey ? { apiKey: data.apiKey } : {}),
        ...(!data.useApiKey && data.password ? { password: data.password } : {}),
      });
    } else {
      createMutation.mutate({
        name: data.name,
        erpType: data.erpType,
        url: data.url,
        database: data.database,
        username: data.username,
        useApiKey: data.useApiKey,
        ...(data.erpType === 'acumatica' ? { acumaticaEndpointVersion: data.acumaticaEndpointVersion } : {}),
        ...(data.useApiKey ? { apiKey: data.apiKey } : { password: data.password }),
      });
    }
  };

  const handleTest = (data: FormData) => {
    setTestResult(null);
    setTestingErpType(data.erpType);
    testMutation.mutate({
      erpType: data.erpType,
      url: data.url,
      database: data.database,
      username: data.username,
      useApiKey: data.useApiKey,
      ...(data.erpType === 'acumatica' ? { acumaticaEndpointVersion: data.acumaticaEndpointVersion } : {}),
      ...(data.useApiKey ? { apiKey: data.apiKey } : { password: data.password }),
    });
  };

  const handleDelete = () => {
    if (deleteConnection) {
      deleteMutation.mutate({ connectionId: deleteConnection.id });
    }
  };

  const handleConfirmConfigChange = () => {
    if (pendingConfigChange && editConnection) {
      const data = pendingConfigChange.formData;
      updateMutation.mutate({
        connectionId: editConnection.id,
        name: data.name,
        url: data.url,
        database: data.database,
        username: data.username,
        ...(data.erpType === 'acumatica' ? { acumaticaEndpointVersion: data.acumaticaEndpointVersion } : {}),
        ...(data.useApiKey && data.apiKey ? { apiKey: data.apiKey } : {}),
        ...(!data.useApiKey && data.password ? { password: data.password } : {}),
        confirmDataReset: true, // User confirmed - proceed with deletion
      });
    }
  };

  const handleCancelConfigChange = () => {
    setPendingConfigChange(null);
    pendingFormDataRef.current = null;
  };

  const handleValidate = (connectionId: string) => {
    setCardTestResult(null);
    setValidatingConnectionId(connectionId);
    validateMutation.mutate({ connectionId });
  };

  const handleSync = (connectionId: string) => {
    setSyncingConnectionId(connectionId);
    syncMutation.mutate({ connectionId });
  };

  const connectionList = (connections || []) as ERPConnection[];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ERP Connections</h1>
          <p className="text-gray-600">Connect your ERP system to sync financial data</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection limit indicator */}
          <div className="text-sm text-gray-500">
            {currentConnections} / {connectionLimit === -1 ? '∞' : connectionLimit} connections
          </div>
          <button
            onClick={() => handleOpenForm()}
            disabled={!canAddConnection}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            title={!canAddConnection ? 'Connection limit reached. Upgrade your plan for more connections.' : undefined}
          >
            Add Connection
          </button>
        </div>
      </div>

      {/* Upgrade prompt if at limit */}
      {!canAddConnection && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium text-amber-800">Connection limit reached</p>
              <p className="text-sm text-amber-700">
                Your current plan allows {connectionLimit} connection{connectionLimit !== 1 ? 's' : ''}. Upgrade to add more ERP connections.
              </p>
            </div>
            <a
              href="/settings/billing"
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
            >
              Upgrade Plan
            </a>
          </div>
        </div>
      )}

      {/* Connection List */}
      {connectionsLoading ? (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : connectionList.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {connectionList.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              onEdit={handleOpenForm}
              onDelete={setDeleteConnection}
              onSync={handleSync}
              onTest={handleValidate}
              isSyncing={syncingConnectionId === connection.id}
              isTesting={validatingConnectionId === connection.id}
              testResult={cardTestResult}
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">No ERP connections configured</h3>
              <p className="text-sm text-gray-500">
                Click "Add Connection" above to connect your ERP system
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Supported ERP Systems */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Supported ERP Systems</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <ErpOption
            name="Odoo"
            description="Connect to Odoo 14+ via XML-RPC"
            status="available"
            logo="/assets/erp/odoo.png"
          />
          <ErpOption
            name="Acumatica"
            description="Connect to Acumatica Cloud ERP"
            status="available"
            logo="/assets/erp/acumatica.png"
          />
          <ErpOption
            name="QuickBooks"
            description="Connect to QuickBooks Online via OAuth 2.0"
            status="available"
            logo="/assets/erp/quickbooks.png"
          />
          <ErpOption
            name="Xero"
            description="Connect to Xero via OAuth 2.0"
            status="coming_soon"
            logo="/assets/erp/xero.svg"
          />
          <ErpOption
            name="Sage"
            description="Connect to Sage Intacct"
            status="coming_soon"
            logo="/assets/erp/sage.svg"
          />
          <ErpOption
            name="NetSuite"
            description="Connect to Oracle NetSuite"
            status="coming_soon"
            logo="/assets/erp/netsuite.svg"
          />
        </div>
      </div>

      {/* Documentation Link */}
      <div className="card bg-primary-50 border-primary-200">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-primary-900">Need help setting up?</h3>
            <p className="text-sm text-primary-700">
              Check out our documentation for step-by-step setup guides
            </p>
          </div>
          <a
            href="https://docs.calculom.com/erp-setup"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-primary-700 border-primary-300 hover:bg-primary-100"
          >
            View Docs
          </a>
        </div>
      </div>

      {/* Form Modal */}
      <ConnectionFormModal
        isOpen={showFormModal}
        editConnection={editConnection}
        onClose={handleCloseForm}
        onSave={handleSave}
        onTest={handleTest}
        isSaving={createMutation.isPending || updateMutation.isPending || qbOAuthMutation.isPending}
        isTesting={testMutation.isPending}
        testResult={testResult}
      />

      {/* Configuration Change Confirmation Modal */}
      {pendingConfigChange && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={handleCancelConfigChange} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Connection Changes</h3>
              <p className="text-gray-600 mb-4">
                Changing the connection parameters will require a full data resync, which may take a few minutes to complete.
              </p>
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Changes detected:</p>
                <ul className="text-sm text-gray-600 list-disc list-inside">
                  {pendingConfigChange.changes.map((change, i) => (
                    <li key={i}>{change}</li>
                  ))}
                </ul>
              </div>
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-medium text-blue-800 mb-1">Data to be refreshed:</p>
                <ul className="text-sm text-blue-700">
                  <li>• {pendingConfigChange.dataToDelete.companies} companies</li>
                  <li>• {pendingConfigChange.dataToDelete.accounts} accounts</li>
                  <li>• {pendingConfigChange.dataToDelete.glEntries} GL entries</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCancelConfigChange}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmConfigChange}
                  disabled={updateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Updating...' : 'Update & Resync'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteConnection}
        connectionName={deleteConnection?.name || ''}
        onClose={() => setDeleteConnection(null)}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}

// ============================================================
// ERP OPTION COMPONENT
// ============================================================

function ErpOption({
  name,
  description,
  status,
  logo,
}: {
  name: string;
  description: string;
  status: 'available' | 'coming_soon' | 'enterprise';
  logo: string;
}) {
  return (
    <div className="border border-gray-200 rounded-lg p-5 hover:border-gray-300 transition-colors flex flex-col items-center text-center">
      <div className="h-16 flex items-center justify-center mb-3">
        <img src={logo} alt={name} className="max-h-full object-contain" style={{ width: '140px' }} />
      </div>
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-medium text-gray-900">{name}</h3>
        {status === 'available' && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
            Available
          </span>
        )}
        {status === 'coming_soon' && (
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            Coming Soon
          </span>
        )}
        {status === 'enterprise' && (
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
            Enterprise
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
}
